import * as React from 'react';
import type { ResolvedGroup } from './palette.ts';

// The settings section showing the app's colour palette, drawn from props
// only: groups of tokens, each swatch the chip, the colour's semantic name,
// what it is used for, what it derives from when it follows other colours
// ("follows Win", "mix of Win and Draw"), then the token and the value it resolves to. A token that
// resolves to nothing shows "—" and no chip. An accordion (a native
// <details>), closed by default; `onOpen` tells the container it was opened,
// so it can read the values again.

export interface ThemePaletteProps {
  groups: ResolvedGroup[];
  /** Whether the accordion starts open; closed by default. */
  defaultOpen?: boolean;
  /** Called when the accordion opens. */
  onOpen?(): void;
}

export function ThemePalette({ groups, defaultOpen, onOpen }: ThemePaletteProps) {
  const toggle = (e: React.SyntheticEvent<HTMLDetailsElement>) => {
    if (e.currentTarget.open) onOpen?.();
  };
  return (
    <details className="panel viz settings-theme" open={defaultOpen || undefined} onToggle={toggle}>
      <summary className="settings-theme-summary" data-track="settings.theme.toggle">
        <h2>Theme</h2>
        <svg className="settings-theme-caret" viewBox="0 0 256 256" aria-hidden="true">
          <path d="M213.66 101.66l-80 80a8 8 0 0 1-11.32 0l-80-80a8 8 0 0 1 11.32-11.32L128 164.69l74.34-74.35a8 8 0 0 1 11.32 11.32Z" />
        </svg>
      </summary>
      <p className="settings-theme-intro t-body">The colours the app draws with, as the current theme sets them.</p>
      {groups.map((g) => (
        <section key={g.title} className="settings-theme-group" aria-label={g.title}>
          <h3 className="settings-theme-heading t-label">{g.title}</h3>
          <ul className="settings-theme-grid">
            {g.tokens.map((t) => (
              <li key={t.token} className="settings-theme-swatch">
                {t.value ? (
                  <span className="settings-theme-chip" style={{ background: t.value }} aria-hidden="true" />
                ) : (
                  <span className="settings-theme-chip settings-theme-chip-empty" aria-hidden="true" />
                )}
                <span className="settings-theme-text">
                  <span className="settings-theme-name t-body">{t.name}</span>
                  {t.use && <span className="settings-theme-use t-caption">{t.use}</span>}
                  {t.derived && <span className="settings-theme-derived t-caption">{t.derived}</span>}
                  <code className="settings-theme-token t-caption">
                    {t.token} · {t.value || '—'}
                  </code>
                </span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </details>
  );
}
